import {format, parse, parseISO} from 'date-fns'

import {formatBusinessTimestamp} from 'src/api/business-time'

export type JobScheduleRangeStyle = 'full' | 'compact'

type JobScheduleParts = {
    startDate: Date | string | null
    endDate?: Date | string | null
    startTime?: string | null
    endTime?: string | null
    style?: JobScheduleRangeStyle
}

/** Normalizes a `Date` or date string to a local `Date` (treats `YYYY-MM-DD` as a calendar day). */
export function toLocalDate(value: Date | string): Date {
    if (value instanceof Date) {
        return value
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number)
        return new Date(year, month - 1, day)
    }

    return parseISO(value)
}

/** Parses API `YYYY-MM-DD` as a local calendar date (avoids UTC day shift). */
export function parseApiDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
}

/** Normalizes API or form time strings to `HH:mm` (minute precision). */
export function parseApiTime(timeStr: string | null): string | null {
    if (!timeStr) {
        return null
    }

    const match = timeStr.match(/^(\d{1,2}):(\d{2})/)
    if (!match) {
        return null
    }

    return `${match[1].padStart(2, '0')}:${match[2]}`
}

/** Serializes a date-only value using its local calendar components. */
export function toApiDateString(date: Date | string): string {
    const localDate = date instanceof Date ? date : toLocalDate(date)
    const year = localDate.getFullYear()
    const month = String(localDate.getMonth() + 1).padStart(2, '0')
    const day = String(localDate.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

/** Serializes a timestamp using the Chicago business calendar date. */
export function toBusinessApiDateString(value: Date | string): string {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value
    }

    return formatBusinessTimestamp(value, 'yyyy-MM-dd')
}

/** Converts `HH:mm` form values to API `HH:mm:00` (seconds always zero). */
export function toApiTimeString(value: string | null): string | null {
    const normalized = parseApiTime(value)
    if (!normalized) {
        return null
    }

    return `${normalized}:00`
}

export function formatJobDate(value: Date | string | null): string {
    if (!value) {
        return '—'
    }

    return format(toLocalDate(value), 'MMM d, yyyy')
}

export function formatJobTime(value: string | null): string {
    const normalized = parseApiTime(value)
    if (!normalized) {
        return '—'
    }

    const parsed = parse(normalized, 'HH:mm', new Date())
    return format(parsed, 'h:mm a')
}

export function formatDateTbcLabel(startTime: string | null): string {
    const timeLabel = startTime ? formatJobTime(startTime) : '—'
    return timeLabel === '—' ? 'date TBC' : `date TBC · ${timeLabel}`
}

function formatRangeDate(value: Date | string, style: JobScheduleRangeStyle): string {
    return format(toLocalDate(value), style === 'compact' ? 'M/d/yy' : 'MMM d, yyyy')
}

function formatScheduleEndpoint(
    date: Date | string | null,
    time: string | null | undefined,
    style: JobScheduleRangeStyle,
): string | null {
    if (!date) {
        return null
    }

    const dateLabel = formatRangeDate(date, style)
    const timeLabel = time ? formatJobTime(time) : null

    if (!timeLabel || timeLabel === '—') {
        return dateLabel
    }

    return style === 'compact' ? `${dateLabel} ${timeLabel}` : `${dateLabel} · ${timeLabel}`
}

export function formatJobScheduleRange({
    startDate,
    endDate = null,
    startTime = null,
    endTime = null,
    style = 'full',
}: JobScheduleParts): string {
    const startLabel = formatScheduleEndpoint(startDate, startTime, style)
    if (!startLabel) {
        return '—'
    }

    const endLabel = endDate ? formatScheduleEndpoint(endDate, endTime, style) : null
    if (!endLabel || endLabel === startLabel) {
        return startLabel
    }

    return `${startLabel} – ${endLabel}`
}

export function jobTimeToMinutes(value: string | null): number | null {
    const normalized = parseApiTime(value)
    if (!normalized) {
        return null
    }

    const [hours, minutes] = normalized.split(':').map(Number)
    return hours * 60 + minutes
}

export function parseDateInputValue(value: string | Date | null): Date | null {
    if (value == null) {
        return null
    }

    if (value instanceof Date) {
        return value
    }

    const [year, month, day] = value.split('-').map(Number)
    if (year == null || month == null || day == null) {
        return null
    }

    return new Date(year, month - 1, day)
}
